/**
 * Typed API errors. The class names match the historical error classes used
 * across the app so existing `instanceof` checks continue to work.
 */

export class RequirePaymentError extends Error {
    constructor(message, entity) {
        super(message)
        this.name = 'RequirePaymentError'
        this.entity = entity
    }
}

export class TokenError extends Error {
    constructor(message) {
        super(message)
        this.name = 'TokenError'
    }
}

export class ResponseError extends Error {
    constructor({ message, errors } = {}) {
        super(message)
        this.name = 'ResponseError'
        this.errors = errors
    }
}

export class ResourceNotFound extends ResponseError {
    constructor(data) {
        super(data || {})
        this.name = 'ResourceNotFound'
    }
}

export class CanceledError extends Error {
    constructor(message) {
        super(message || 'canceled')
        this.name = 'CanceledError'
        this.isCanceled = true
    }
}

/**
 * Normalize an axios error into one of the typed errors above.
 *
 *  402 → RequirePaymentError (with `entity` from response body)
 *  401 + "Unauthenticated." → TokenError
 *  404 → ResourceNotFound
 *  other response with body → ResponseError
 *  no response (network) → plain Error with `.original` set
 *  axios-cancel / abort → CanceledError
 */
export function normalizeAxiosError(error) {
    if (
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError'
    ) {
        return new CanceledError(error.message)
    }

    const res = error?.response
    if (!res) {
        const e = new Error(error?.message || 'Network error')
        e.original = error
        return e
    }

    const { status, data } = res

    if (status === 402) {
        const out = new RequirePaymentError(data?.message, data?.entity)
        out.status = 402
        out.response = res
        return out
    }
    if (status === 401 && data?.message === 'Unauthenticated.') {
        const out = new TokenError(error.message)
        out.status = 401
        out.response = res
        return out
    }
    if (status === 404) {
        const out = new ResourceNotFound(data || { message: 'Not found' })
        out.status = 404
        out.response = res
        return out
    }
    if (data) {
        const out = new ResponseError(data)
        out.status = status
        out.response = res
        return out
    }

    const fallback = new Error(error.message || `HTTP ${status}`)
    fallback.status = status
    fallback.response = res
    return fallback
}
